/**
 * Reconciliation for the per-chat scheduled-messages panel.
 *
 * On a successful send the backend patches the user-message to `confirmed` +
 * `deleted: true` (deleteUserMessageAndCreateLog) — it leaves the queue. That patch
 * does not always reach an open client live, so the panel keeps rendering the row at
 * its last-seen status ("Sent - not confirmed"), and only a full page refresh clears
 * it. A refetch alone does NOT fix this: the server simply omits the row, and the DB
 * thunks upsert what they receive, so the stale copy survives in redux. The stale rows
 * have to be pruned explicitly — that is what this computes.
 *
 * The `snapshotIds` guard is what makes the prune safe against a concurrent create: a
 * message added to the store AFTER the refetch was issued is not in the snapshot, so a
 * server response that predates it can never delete it.
 */
export interface StaleChatMessageIdsParams {
  /** Panel rows present in the store when the refetch was ISSUED. */
  snapshotIds: string[];
  /** Panel rows present in the store now (the response has come back). */
  localIds: string[];
  /** Row ids the server just reported as still queued for this chat. */
  serverIds: string[];
  /** Optimistic rows not yet persisted — never pruned. */
  isTempId?: (id: string) => boolean;
}

export function staleChatMessageIds({
  snapshotIds,
  localIds,
  serverIds,
  isTempId,
}: StaleChatMessageIdsParams): string[] {
  const serverSet = new Set(serverIds);
  const localSet = new Set(localIds);
  const seen = new Set<string>();

  return snapshotIds.filter((id) => {
    if (!id || seen.has(id)) return false;
    seen.add(id);
    // Gone from the panel already (deleted, or moved out of the shown statuses).
    if (!localSet.has(id)) return false;
    // The server still lists it — genuinely queued.
    if (serverSet.has(id)) return false;
    // Optimistic row: the server has never heard of it yet.
    if (isTempId?.(id)) return false;
    return true;
  });
}
